import React, { FC, forwardRef, useState, ElementType, useRef } from 'react';
import {
  Collapse,
  Backdrop,
  ClickAwayListener,
  Box,
  BoxProps,
  Portal,
  Container,
  ClickAwayListenerProps,
  ContainerProps,
  PortalProps,
  BackdropProps,
  CollapseProps,
  useMediaQuery,
  Theme,
  useTheme,
} from '@mui/material';
import { ChevronDownIcon } from '@/src/components/icon/stroke/ChevronDownIcon';
import { HeaderNavigationLink, HeaderNavigationLinkProps } from './HeaderNavigationLink';

export interface HeaderNavigationHeroProps extends HeaderNavigationLinkProps {
  /**
   * Wrap the content into container
   * @default false
   */
  inContainer?: boolean;
  /**
   * Controls appearance of backdrop when content opens
   * @default false
   */
  disableBackdrop?: boolean;
  /**
   * Props applied to ClickAwayListener
   */
  clickAwayListenerProps?: Omit<ClickAwayListenerProps, 'children'>;
  /**
   * Props applied to main wrapper of content
   */
  contentProps?: BoxProps<ElementType>;
  /**
   * Props applied to main wrapper before collapsible
   */
  wrapperProps?: BoxProps<ElementType>;
  /**
   * Props applied to Container if inContainer property is provided
   */
  containerProps?: ContainerProps;
  /**
   * Props applied to Collapse component
   */
  collapseProp?: CollapseProps;
  /**
   * Props applied to Portal if disableBackdrop property is not provided
   */
  portalProps?: PortalProps;
  /**
   * Props applied to Backdrop if disableBackdrop property is not provided
   */
  backdropProps?: BackdropProps;
}

/**
 * HeaderNavigationLink with the integration of the complex hero menu
 */
export const HeaderNavigationHero: FC<HeaderNavigationHeroProps> = forwardRef(
  (
    {
      children,
      sx,
      label,
      icon,
      extend,
      inContainer = false,
      disableBackdrop = false,
      onClick,
      clickAwayListenerProps,
      contentProps,
      wrapperProps,
      containerProps,
      collapseProps,
      portalProps,
      backdropProps,
      ...rest
    },
    ref,
  ) => {
    const buttonRef = useRef(null);
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const downLg = useMediaQuery<Theme>(theme.breakpoints.down('lg'));
    const mobile = extend === undefined ? downLg : extend;
    const { sx: backdropSx, ...restBackdrop } = backdropProps ?? {};
    const { sx: wrapperSx, ...restWrapper } = wrapperProps ?? {};
    const { sx: contentSx, ...restContent } = contentProps ?? {};

    return (
      <>
        <HeaderNavigationLink
          active={open}
          extend={extend}
          icon={
            icon || (
              <ChevronDownIcon
                sx={{
                  '&&&': {
                    fontSize: extend === undefined ? { xs: '20px', lg: '14px' } : ['20px', '14px'][+!extend],
                  },
                  color:
                    extend === undefined
                      ? {
                          xs: theme.palette.mode === 'dark' ? 'onSurface.os300' : 'onSurface.os700',
                          lg: 'currentColor',
                        }
                      : [theme.palette.mode === 'dark' ? 'onSurface.os300' : 'onSurface.os700', 'currentColor'][
                          +!extend
                        ],
                  transform: `rotate(${open ? '180' : '0'}deg)`,
                  transition:
                    extend === undefined
                      ? {
                          xs: theme.transitions.create('transform', {
                            duration: theme.transitions.duration.shortest,
                          }),
                          lg: 'none',
                        }
                      : [
                          theme.transitions.create('transform', {
                            duration: theme.transitions.duration.shortest,
                          }),
                          'none',
                        ][+!extend],
                }}
              />
            )
          }
          onClick={(e) => {
            onClick?.(e);
            setOpen((prev) => !prev);
          }}
          sx={{
            justifyContent:
              extend === undefined
                ? { xs: 'space-between', lg: 'flex-start' }
                : ['space-between', 'flex-start'][+!extend],
            ...sx,
          }}
          ref={ref ?? buttonRef}
          data-testid="HeaderNavigationHero"
          {...rest}
        >
          {label}
        </HeaderNavigationLink>
        <ClickAwayListener
          onClickAway={(e) =>
            !((ref as React.RefObject<HTMLButtonElement>) ?? buttonRef)?.current?.contains(e?.target as Node) &&
            setOpen(false)
          }
          {...clickAwayListenerProps}
        >
          <Box
            sx={{
              position: extend === undefined ? { xs: 'static', lg: 'absolute' } : ['static', 'absolute'][+!extend],
              zIndex: '10',
              left: '0',
              right: '0',
              top: theme.mixins.toolbar.minHeight,
              maxHeight: `calc(100vh - ${theme.mixins.toolbar.minHeight})`,
              overflow: 'auto',
              ...wrapperSx,
            }}
            {...restWrapper}
          >
            <Collapse in={open} {...collapseProps}>
              <Box
                sx={{
                  backgroundColor:
                    extend === undefined
                      ? {
                          xs: 'unset',
                          lg: theme.palette.mode === 'dark' ? 'surface.primary' : 'background.primary',
                        }
                      : ['unset', theme.palette.mode === 'dark' ? 'surface.primary' : 'background.primary'][+!extend],
                  py: extend === undefined ? { xs: 0, lg: 2 } : [0, 2][+!extend],
                  px: extend === undefined ? { xs: 1, lg: 2 } : [1, 2][+!extend],
                  ...(inContainer && {
                    px: 0,
                  }),
                  ...contentSx,
                }}
                {...restContent}
              >
                {inContainer ? (
                  <Container maxWidth="xl" {...containerProps}>
                    {children}
                  </Container>
                ) : (
                  children
                )}
              </Box>
            </Collapse>
          </Box>
        </ClickAwayListener>
        {!disableBackdrop && !mobile && (
          <Portal {...portalProps}>
            <Backdrop open={open} sx={{ zIndex: theme.zIndex.appBar - 50, ...backdropSx }} {...restBackdrop} />
          </Portal>
        )}
      </>
    );
  },
);
